// src/pages/Products.js
import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { 
  Grid, 
  Typography, 
  CircularProgress, 
  Box, 
  useMediaQuery, 
  Breadcrumbs 
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ProductCard from '../components/Product/ProductCard/ProductCard';
import { getDepartmentFromPath } from '../utils/urlUtils';

const Products = () => {
  const { category: categoryParam } = useParams();
  const location = useLocation();
  const categoryName = decodeURIComponent(categoryParam);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get current department from URL
  const currentDepartment = getDepartmentFromPath(location.pathname);

  useEffect(() => {
    const fetchCategoryAndProducts = async () => {
      try {
        // First get the category and its subcategories
        const { data: categoryData, error: categoryError } = await supabase
          .from('categories')
          .select('id, name, parent_id')
          .ilike('name', `%${categoryName}%`)
          .single();

        if (categoryError) throw categoryError;
        setCategory(categoryData);

        // Then get products in this category and its subcategories
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select(`
            *,
            category:categories(id, name),
            product_colors(
              id,
              color_id,
              color_order,
              is_home_color,
              colors(name),
              product_color_sizes(size, stock),
              product_color_images(image_url, image_order)
            )
          `)
          .eq('category_id', categoryData.id);

        if (productsError) throw productsError;

        // Transform the data to match what ProductCard expects
        const transformedProducts = await Promise.all(productsData.map(async (product) => {
          try {
            // Get color data and sort by color_order
            const { data: colorData, error: colorError } = await supabase
              .from('product_colors')
              .select(`
                *,
                colors(name),
                product_color_images(image_url, image_order)
              `)
              .eq('product_id', product.id)
              .order('color_order');
              
            if (colorError) throw colorError;
            
            // Find the home color first, fallback to first color if no home color is set
            let homeColor = colorData.find(color => color.is_home_color) || colorData[0] || null;
            let stock = 0;
            let images = ['/placeholder-image.png'];
            
            if (homeColor) {
              // Get sizes for the home color - only need stock info now
              const { data: sizeData, error: sizeError } = await supabase
                .from('product_color_sizes')
                .select('stock')
                .eq('product_color_id', homeColor.id)
                .order('size');
                
              if (sizeError) throw sizeError;
              
              if (sizeData && sizeData.length > 0) {
                // Sum up stock across all sizes for this color
                stock = sizeData.reduce((total, size) => total + (size.stock || 0), 0);
              }
              
              // Get images for the home color, ordered by image_order
              const { data: imageData, error: imageError } = await supabase
                .from('product_color_images')
                .select('*')
                .eq('product_color_id', homeColor.id)
                .order('image_order');
                
              if (imageError) throw imageError;
              
              if (imageData && imageData.length > 0) {
                images = imageData.map(img => img.image_url);
              }
            }
            
            const categoryName = 
              (product.category && product.category.name) || 
              'غير مصنف';
            
            return {
              id: product.id,
              name_ar: product.name_ar,
              description_ar: product.description_ar,
              price: product.price || 0, // Get price from products table
              newPrice: product.new_price || null, // Get new_price from products table
              stock: stock,
              images: images,
              product_colors: colorData || [],
              home_color: homeColor,
              category: categoryName,
              category_id: product.category_id
            };
          } catch (error) {
            console.error(`Error processing product ${product.id}:`, error);
            return {
              id: product.id,
              name_ar: product.name_ar,
              description_ar: product.description_ar,
              price: product.price || 0, // Get price from products table
              newPrice: product.new_price || null, // Get new_price from products table
              stock: 0,
              images: ['/placeholder-image.png'],
              product_colors: [],
              home_color: null,
              category: 'غير مصنف',
              category_id: product.category_id
            };
          }
        }));

        setProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (categoryName) {
      fetchCategoryAndProducts();
    }
  }, [categoryName]);

  // Helper function to get department path for breadcrumbs
  const getDepartmentPath = () => {
    const path = location.pathname;
    if (path.includes('/department/men')) return '/department/men';
    if (path.includes('/department/women')) return '/department/women';
    if (path.includes('/department/children')) return '/department/children';
    return '/department/children';
  };

  return (
    <div>
      {/* Breadcrumbs Section */}
      <Box sx={{ px: 2, py: 2, bgcolor: 'background.paper' }}>
        <Breadcrumbs aria-label="breadcrumb" separator="<" sx={{ fontSize: '16px', fontWeight: 'bold' }}>
          <Link 
            to="/department/children" 
            style={{ 
              textDecoration: 'none', 
              color: theme.palette.primary.main 
            }}
          >
            الرئيسية
          </Link>
          <Link 
            to={getDepartmentPath()} 
            style={{ 
              textDecoration: 'none', 
              color: theme.palette.primary.main 
            }}
          >
            {currentDepartment}
          </Link>
          <Typography color="text.primary">
            {category?.name || categoryName}
          </Typography>
        </Breadcrumbs>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" pt={4}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Typography variant="h6" align="center" mt={4}>
          لا توجد منتجات في هذه الفئة
        </Typography>
      ) : (
        <Box sx={{ mb: 4 }}>
          {/* Mobile Layout - Match Home page card dimensions */}
          {isMobile ? (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)', // 2 cards per row
              gap: 1,
              px: 1,
              // Each card container will have consistent dimensions
              '& > *': {
                minWidth: '150px', // Match Home page dimensions
                maxWidth: '150px',
                justifySelf: 'center' // Center cards in their grid cells
              }
            }}>
              {products.map((product) => (
                <Box key={product.id}>
                  <ProductCard product={product} />
                </Box>
              ))}
            </Box>
          ) : (
            /* Desktop Layout - Use original Grid approach */
            <Grid 
              container 
              spacing={3} 
              sx={{ px: 2 }}
            >
              {products.map((product) => (
                <Grid 
                  item 
                  sm={6}  // 2 cards per row on small screens
                  md={4}  // 3 cards per row on medium screens
                  lg={3}  // 4 cards per row on large screens
                  key={product.id}
                >
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
    </div>
  );
};

export default Products;
